<template>
  <div>
    <input ref="fileInput" type="file" multiple style="display: none" @input="selectFileFromInput($event)" />
    <slot name="upload-button" :activateFileInput="activateFileInput">
      <a-btn outlined :append-icon="require('@/assets/icons/up_arrow.svg')" @click="activateFileInput">{{label}}</a-btn>
    </slot>

    <div class="files-container">
      <div
        v-for="(file, i) in localFiles"
        :key="i"
      >
        <slot
          name="file-item"
          :file="file"
          :index="i"
          :viewFile="() => viewFile(file)"
          :removeFile="() => removeFile(i)"
        >
          <div
            class="file-panel"
          >
            <img src="@/assets/icons/file.svg" class="mr-3" />
            <span class="text--bold">{{ file.filename }}</span>
            <div class="flex-grow-1" />

            <img
              v-if="file.status === 'failed'"
              class="mr-3"
              src="@/assets/icons/warning.svg"
              v-tooltip="{ content: 'Failed to upload', delay: 200}"
            />
            <b-spinner
              v-else-if="file.status === 'pending' || file.status === 'uploading'"
              :value="file.progress"
              small
              class="mr-3"
              />
            <img
              v-else-if="file.key"
              src="@/assets/icons/remove-red-eye.svg"
              class="mr-3"
              style="cursor: pointer;"
              @click="viewFile(file)"
            />

            <img
              v-if="!file.status || file.status === 'failed'"
              src="@/assets/icons/trash-mdi.svg"
              class="mr-3" style="cursor: pointer"
              @click="removeFile(i)" />
          </div>
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import ABtn from "@/components/SelfService/a-btn";
import {v4 as uuidV4} from 'uuid';
import API from "@/api";
import Vue from "vue";
export default {
  name: "UploadManager",
  components: {ABtn},
  props: {
    label: {
      type: String,
      default: 'Select a file to upload'
    },
    files: {
      type: Array,
      default: () => ([])
    }
  },
  data(){
    return {
      localFiles: []
    }
  },
  watch: {
    files: {
      immediate: true,
      handler() {
        this.localFiles = JSON.parse(JSON.stringify(this.files))
      }
    }
  },
  methods: {
    activateFileInput(){
      this.$refs['fileInput'].click()
    },
    selectFileFromInput() {
      if (this.$refs.fileInput.files.length) {
        const uploadFile = this.$refs.fileInput.files[0];
        let fileObj = {filename: uploadFile.name, progress: 1, status: 'uploading'}
        // clear the file input element
        this.$refs.fileInput.value = '';

        const progress = (computable, loaded, total) =>
        {
          if(!computable) return;
          fileObj.progress = Math.round((loaded/total * 100));
        };

        this.localFiles.push(fileObj)

        this.secureUpload(uploadFile, progress)
        .then((file) => {
          Object.assign(fileObj, file);

          Vue.set(fileObj, 'filename', file.filename)
          Vue.delete(fileObj, 'status');
          Vue.delete(fileObj, 'progress');
          this.$emit('fileAdded', fileObj)
        })
        .catch(() => {
          fileObj.status = 'failed';
        })
      }
    },
    secureUpload(file, progress){
      return new Promise((resolve, reject) => {
        const Key = uuidV4();
        const request = new XMLHttpRequest();
        let abortedPreUpload = false;

        API.getS3PUTURL(Key, file.type)
          .then((response) => {
              if(abortedPreUpload) return;
              // fieldName is the name of the input field
              // file is the actual file object to send
              const formData = new FormData();
              Object.keys(response.fields).forEach(key => {
                formData.append(key, response.fields[key]);
              });
              // Actual file has to be appended last.
              formData.append("file", file);

              request.open('POST', response.url);

              // Should call the progress method to update the progress to 100% before calling load
              // Setting computable to false switches the loading indicator to infinite mode
              request.upload.onprogress = (e) => {
                progress(e.lengthComputable, e.loaded, e.total);
              };

              // Should call the load method when done and pass the returned server file id
              // this server file id is then used later on when reverting or restoring a file
              // so your server knows which file to return without exposing that info to the client
              request.onload = () => {
                if (request.status >= 200 && request.status < 300) {
                  const {name: filename, type: mimetype, size} = file;
                  const {bucket} = response.fields;
                  const {url} = response;

                  API.createFile({
                    filename,
                    url: `${url}/${Key}`,
                    mimetype,
                    size,
                    bucket,
                    key: Key,
                  })
                    .then(({file}) => {
                      // the load method accepts either a string (id) or an object
                      resolve(file);
                    })
                    .catch(() => reject(new Error('Could not save uploaded file')));
                } else {
                  // Can call the error method if something is wrong, should exit after
                  reject(new Error('Could not upload file'));
                }
              };

              request.ontimeout = () => {
                const error = new Error('Upload request timeout');
                console.error(error);
                reject (error);
              }

              request.onerror = () => {
                const error = new Error('Upload request error');
                console.error(error);
                reject (error);
              }

              request.send(formData);
          })
          .catch((e) => {
            console.error('Upload error', e)
            reject(new Error('Could not upload file'));
          })
    }
)
    },
    viewFile(file){
      const { key } = file
      if(!key) return;

      API.getS3GETURL(key)
        .then(({signedURL}) => {
          API.downloadFile(signedURL, file.filename);
        })
        .catch((e) => {
          console.error(e)
        })
    },
    removeFile(index){
      // TODO: We need to actually remove the file from the files table and S3, not just the task
      const file = this.localFiles[index]
      this.localFiles.splice(index, 1)
      if(file.id) this.$emit('removeFile', file.id)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/SelfService/colors.scss';
@import '~@/styles/SelfService/spacing.scss';

.files-container {
  .file-panel {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #FFFFFF;
    border: 1px solid $primary-disabled;
    box-sizing: border-box;
    border-radius: 12px;
    padding: 22px;

    &:first-child{
      margin-top: $spacing--l;
    }

    &:not(:last-child){
      margin-bottom: $spacing--m;
    }
  }
}
</style>
